import React from 'react';

import { Button, Flex, Layout, Row, Segmented, Table, Typography } from 'antd';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { EpdAwaitingRequestSchema, EpdDocumentKind, EpdRegistryRowSchema } from '@app/models/orders';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { useTableParams } from '@app/services/hooks/useTableParams';
import { selectIsCounterpartySpace } from '@app/store/currentSessionReducer';
import { EpdError } from '@components/EpdError';
import { EpdSigningModal } from '@components/EpdSigningModal';
import { ISigningTarget } from '@components/EpdSigningModal/interfaces';
import { HeaderTitle } from '@components/HeaderTitle';
import { Paper } from '@components/Paper';

import { EpdFilters } from '../../components/EpdFilters';
import { RowsActions } from '../../components/RowsActions';
import { getColumns } from '../../data/constants';
import { useEpdDocumentsQuery } from '../../hooks';
import { IEpdFilters } from '../../interfaces';

const EMPTY_FILTERS: IEpdFilters = { awaiting_me: false, discrepancy: false };

export const List: React.FC = () => {
  const epdT = useTranslation(NSKeys.epd);
  const commonT = useTranslation(NSKeys.common);
  const isCounterparty = useLocalSelector(selectIsCounterpartySpace);
  const canManage = useLocalSelector((state) => !!state.currentSession.ordersContext?.scope.$permissions.manage_epd);
  const [params, setParams] = useSearchParams();
  const kind =
    params.get('tab') === EpdDocumentKind.ForwardingReceipt
      ? EpdDocumentKind.ForwardingReceipt
      : EpdDocumentKind.ForwardingOrder;
  const { current, pageSize, setPagination } = useTableParams();
  const [filtersByKind, setFiltersByKind] = React.useState<Partial<Record<EpdDocumentKind, IEpdFilters>>>({});
  const filters = filtersByKind[kind] || EMPTY_FILTERS;
  const query = useEpdDocumentsQuery({ ...filters, kind, limit: pageSize, offset: (current - 1) * pageSize });
  const [selected, setSelected] = React.useState<React.Key[]>([]);
  const [signingTargets, setSigningTargets] = React.useState<ISigningTarget[]>([]);
  const rows = query.data?.items || [];
  const toTarget = (request: EpdAwaitingRequestSchema, number: string): ISigningTarget => ({
    uid: request.uid,
    number,
    title: request.title,
  });
  const targets = rows.flatMap((row) =>
    selected.includes(row.uid) && row.request?.can_sign ? [toTarget(row.request, row.number)] : []
  );
  const changeFilters = (next: IEpdFilters, restored: boolean) => {
    if (isEqual(next, filters)) return;
    setFiltersByKind((values) => ({ ...values, [kind]: next }));
    setSelected([]);
    if (!restored) setPagination(1, pageSize);
  };
  const columns = getColumns(epdT.t, kind, isCounterparty);
  columns.push({
    key: 'action',
    title: '',
    render: (_, row) => {
      const request = row.request;
      return (
        <Flex vertical align="flex-start" gap={4}>
          {request?.signer && <Typography.Text>{request.signer.name}</Typography.Text>}
          {request?.can_sign && (
            <Button
              type="link"
              disabled={query.isFetching}
              onClick={() => setSigningTargets([toTarget(request, row.number)])}
            >
              {epdT.t('sign')}
            </Button>
          )}
        </Flex>
      );
    },
  });

  return (
    <Layout>
      <Layout.Header>
        <Row justify="space-between" align="middle" wrap={false}>
          <HeaderTitle title={epdT.t('title')}>
            <Segmented<EpdDocumentKind>
              value={kind}
              onChange={(value) => {
                setSelected([]);
                setParams((currentParams) => {
                  const next = new URLSearchParams(currentParams);
                  next.set('tab', value);
                  next.set('current', '1');
                  return next;
                });
              }}
              options={Object.values(EpdDocumentKind).map((value) => ({
                value,
                label: epdT.t(`tabs.${value}` as const),
              }))}
            />
          </HeaderTitle>
        </Row>
      </Layout.Header>
      <Paper style={{ margin: 24, padding: 24 }}>
        <Flex vertical gap={16}>
          {!!targets.length && (
            <RowsActions count={targets.length} disabled={query.isFetching} onSign={() => setSigningTargets(targets)} />
          )}
          <EpdFilters
            key={kind}
            kind={kind}
            total={query.isError ? undefined : query.data?.total}
            onChange={changeFilters}
          />
          {query.isError ? (
            <>
              <EpdError error={query.error} />
              <Button onClick={() => query.refetch()}>{commonT.t('buttonReload')}</Button>
            </>
          ) : (
            <Table<EpdRegistryRowSchema>
              rowKey="uid"
              className="tableClassname"
              columns={columns}
              dataSource={rows}
              loading={query.isFetching}
              scroll={{ x: true }}
              rowSelection={
                canManage
                  ? {
                      selectedRowKeys: selected,
                      onChange: setSelected,
                      getCheckboxProps: (row) => ({ disabled: !row.request?.can_sign || query.isFetching }),
                    }
                  : undefined
              }
              pagination={{
                current,
                pageSize,
                total: query.data?.total,
                showSizeChanger: true,
                position: ['bottomCenter'],
                onChange: (page, size) => {
                  setSelected([]);
                  setPagination(page, size);
                },
                showTotal: (total, range) => `${range[0]}–${range[1]} ${commonT.t('paginationOf')} ${total}`,
              }}
            />
          )}
        </Flex>
      </Paper>
      {!!signingTargets.length && (
        <EpdSigningModal
          targets={signingTargets}
          onClose={() => {
            setSigningTargets([]);
            setSelected([]);
          }}
        />
      )}
    </Layout>
  );
};
